import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from, switchMap, tap } from 'rxjs';
import { withPdfPages } from '../pdf-pages/with-pdf-pages';
import { FileObject } from './../interfaces/file-object';
import { PatientService } from './patient.service';
import { SocketService } from './socket.service';

/**
 * `PatientRiskItem`, PATIENT-SCOPED — J1 of `tasks/risk-management.md`.
 *
 * A direct copy of `patient-task.service.ts`: one `BehaviorSubject`, reloaded off
 * `currentPatient$`, kept live by socket events filtered on the current patient. §6.J is explicit
 * that this is the SECOND of two services and not a substitute for the first — `RiskItemService`
 * (G1) is group-scoped and feeds the register/filters tab; this one feeds the patient file and the
 * clinical timeline (D11), exactly as `TaskService` and `PatientTaskService` already split.
 *
 * ── ONE THING IS GENUINELY DIFFERENT FROM THE TEMPLATE ───────────────────────────────────
 *
 * `PatientRiskItem.patientId` is NULLABLE (§5c) — most facility issues name no patient at all
 * ("pharmacy had no ceftriaxone"). Those rows must appear in the group register and NOWHERE in
 * any patient's file or timeline. The `patientId === currentPatientId` guard below delivers that
 * for free: `null !== <uuid>` for every patient, so a non-patient item is structurally invisible
 * here rather than being filtered by a rule someone has to remember (D11). Do not "fix" the guard
 * to tolerate nulls.
 *
 * `patientRiskItems$` is consumed by the clinical timeline (J2/J3), which reads `dateOfIssue` —
 * NOT `createdAt` — as the item's date. Items are returned by the API already ordered
 * `dateOfIssue DESC, createdAt DESC`; that order is preserved on load and is not re-established
 * after a socket append, because every consumer sorts for itself.
 */
@Injectable({
  providedIn: 'root'
})
export class PatientRiskItemService {
  /**
   * Mounted at `router.use('/', patientRouter)` under `/api`, so `/risk-items` here is
   * `routes/clinical/patientRoutes.js`'s `router.post('/risk-items', ...)`. Same shape as
   * `PatientTaskService`'s `api/tasks`.
   */
  private apiUrl = `api/risk-items`;

  private patientRiskItemsSubject = new BehaviorSubject<any[] | null>(null);
  private editingRiskItemSubject = new BehaviorSubject<any>(null);

  patientRiskItems$ = this.patientRiskItemsSubject.asObservable();
  editingRiskItem$ = this.editingRiskItemSubject.asObservable();

  constructor(
    private http: HttpClient,
    private patientService: PatientService,
    private socketService: SocketService
  ) {
    this.subscribeToCurrentPatient();
    this.setupSocketListeners();
  }

  private subscribeToCurrentPatient(): void {
    this.patientService.currentPatient$.pipe(
      switchMap(patient => {
        if (patient) {
          // Cleared first so the previous patient's register never shows under this one, even
          // for the width of one request.
          this.patientRiskItemsSubject.next([]);
          return this.loadRiskItemsByPatient(patient.id);
        } else {
          this.patientRiskItemsSubject.next([]);
          return [];
        }
      }),
      tap((results: any) => {
        const items = results ? results.data : [];
        this.patientRiskItemsSubject.next(items);
      })
    ).subscribe();
  }

  private setupSocketListeners(): void {

    let currentPatientId: string | null = null;

    this.patientService.currentPatient$.subscribe(patient => {
      currentPatientId = patient ? patient.id : null;
    });

    // D4 — `riskItemAdded`. The guard is on `patientId`, and a non-patient item (patientId NULL)
    // therefore never lands here. See the class header: that is the design, not an oversight.
    this.socketService.listen('riskItemAdded', 'patient-risk-item-service').subscribe(newInfo => {
      if (newInfo.patientId === currentPatientId) {
        const current = this.patientRiskItemsSubject.getValue() || [];
        // The controller emits into the whole practice room, so the client that created the item
        // receives its own event back. Replacing rather than appending on a known id keeps the
        // list idempotent if a future caller ever also inserts locally — a duplicated row in a
        // register that gets exported onto a letterhead is worse than a missed animation.
        const exists = current.some(item => item.id === newInfo.id);
        const updated = exists
          ? current.map(item => item.id === newInfo.id ? newInfo : item)
          : [...current, newInfo];
        this.patientRiskItemsSubject.next(updated);
      }
    });

    this.socketService.listen('riskItemUpdated', 'patient-risk-item-service').subscribe(updatedInfo => {
      if (updatedInfo.patientId === currentPatientId) {
        const current = this.patientRiskItemsSubject.getValue() || [];
        const updated = current.map(item => item.id === updatedInfo.id ? updatedInfo : item);
        this.patientRiskItemsSubject.next(updated);
      }
    });

    // The delete is SOFT server-side (`active: false`), and the emitted payload is the whole row.
    // The list still drops it: `loadRiskItemsByPatient` filters on `active: true`, so keeping it
    // would make the live list disagree with the list after a refresh.
    this.socketService.listen('riskItemDeleted', 'patient-risk-item-service').subscribe(deletedInfo => {
      if (deletedInfo.patientId === currentPatientId) {
        const current = this.patientRiskItemsSubject.getValue() || [];
        const updated = current.filter(item => item.id !== deletedInfo.id);
        this.patientRiskItemsSubject.next(updated);
      }
    });

  }

  /** GET /api/risk-items/patient/:patientId */
  loadRiskItemsByPatient(patientId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get<any>(`${this.apiUrl}/patient/${patientId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /**
   * POST /api/risk-items
   *
   * `facilityChoice` — `{ hospitalId, hospitalName, wardName, location }` — IS part of this
   * payload, and the server stores it verbatim (§12, 2026-08-31). This reverses the earlier rule
   * that the four facility columns were server-only.
   *
   * Send it ONLY when the doctor actually chose a facility on the add form. Omit it and the server
   * falls back to `utils/riskHospitalResolver.js` (§4) exactly as before — omission means "you
   * decide", not "clear it".
   *
   * The server does not validate the choice. It is trusted because the only UI that produces one
   * offers hospitals from the group-scoped `hospitals$`, and because it is the doctor correcting
   * their own governance record. §12 records the residual risk and the one query that would close
   * it, deliberately not added.
   *
   * `appointmentId` IS accepted and is not a column: it names the encounter for §4 branch 2 and
   * is consumed by the resolver.
   *
   * The response carries `data.resolvedBy` — which branch of §4 fired — for the caller to show
   * the doctor what the row actually ended up naming.
   */
  addRiskItem(riskItemData: any): Observable<any> {
    const token = localStorage.getItem('token');

    // §28 / P4 — PHOTOGRAPHS. When the payload carries un-uploaded attachments the request goes up
    // as multipart instead of JSON; with none, the body is the identical JSON it always was. See
    // `toRiskFormData` for why the switch is here and not at every call site.
    if (hasNewMedia(riskItemData)) {
      return from(toRiskFormData(riskItemData).then((formData) => withPdfPages(formData))).pipe(
        switchMap(formData => this.http.post<any>(this.apiUrl, formData, {
          headers: { Authorization: `Bearer ${token}` }
        }))
      );
    }

    return this.http.post<any>(this.apiUrl, riskItemData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /**
   * PUT /api/risk-items/:id
   *
   * The server whitelists `category, severity, dateOfIssue, issue, notes, status`. Anything else
   * in the body is IGNORED, not refused — the §4 facility snapshot is immutable after creation and
   * `quote` is write-once evidence (D8) — so the whole object can be round-tripped safely.
   */
  updateRiskItem(id: string, data: any): Observable<any> {
    const token = localStorage.getItem('token');

    // §28 / P4, the edit side. Note what does NOT change: `updateStatus` in the register and
    // `saveNotes` in the detail dialog send one narrow field, carry no `media` key, and therefore
    // still go up as plain JSON — and the server prunes NOTHING when `existingMedia` is absent, so
    // a status change cannot delete the photographs. Both halves of that guard are deliberate.
    if (hasNewMedia(data)) {
      return from(toRiskFormData(data).then((formData) => withPdfPages(formData))).pipe(
        switchMap(formData => this.http.put<any>(`${this.apiUrl}/${id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        }))
      );
    }

    return this.http.put<any>(`${this.apiUrl}/${id}`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /** DELETE /api/risk-items/:id — soft (`active: false`) server-side. */
  deleteRiskItem(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.delete<any>(`${this.apiUrl}/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  setEditingRiskItem(riskItem: any | null) {
    this.editingRiskItemSubject.next(riskItem);
  }
}

// ═══════════════════════════════════════════════════════════════════════════════════════
// MEDIA — §28 / P4-P5. The multipart envelope, in ONE place.
// ═══════════════════════════════════════════════════════════════════════════════════════
//
// > *"they took a photo of the instrument in theatre, then are later doing their notes, this risk
// > item gets flagged, they pic the image from gallery and attach it"*
//
// ── WHY THIS LIVES IN THE SERVICE AND NOT IN THE THREE COMPONENTS ──────────────────────
//
// `app-file-upload` hands back `FileObject`s whose `url` is a `blob:` URL created by
// `URL.createObjectURL` — the bytes are in the browser, not on the object. Turning those into an
// HTTP body means an async `fetch(url).then(r => r.blob())` per file, which is why
// `patient-note-detail.saveNote` gates its request behind a `Promise.all`.
//
// D15 puts the upload control on THREE surfaces. Copying that promise-then-post dance into all
// three is three chances to get the field naming wrong, and the field naming is a contract with
// `patientRiskItemController`. Doing it once here means each component only ever hands the service
// a plain object with a `media` array, exactly as it already hands it `facilityChoice`.
//
// ── THE FIELD NAMES ARE NOT NEW, AND MUST NOT BE ───────────────────────────────────────
//
// `data` (the whole payload as one JSON string) + `newMedia0`, `newMedia1`, … is precisely what
// `patient-note-detail.saveNote` has always sent for note media, and what the server already knows
// how to read. §28 P5 is explicit that this convention is REUSED rather than a second one invented.

/**
 * True when the payload carries at least one attachment that is not yet on the server.
 *
 * A `FileObject` with an `id` is an EXISTING row the gallery is round-tripping; only one WITHOUT
 * an id holds new bytes. So an edit that merely re-displays two saved photographs stays JSON —
 * re-uploading them would duplicate the blobs on every save.
 */
function hasNewMedia(payload: any): boolean {
  return Array.isArray(payload?.media) && payload.media.some((file: FileObject) => file && !file.id);
}

/**
 * Builds the multipart body: `data` as JSON, plus one `newMedia{index}` part per new attachment.
 *
 * Two things are done deliberately:
 *
 * 1. `media` is STRIPPED from the JSON and replaced by `existingMedia`, the ids the gallery still
 *    holds. The server prunes anything active and absent from that list, which is how a deletion
 *    travels — there is no separate delete endpoint and none was added. `existingMedia` is only
 *    ever set when the caller supplied a `media` array at all, preserving the "absent means do not
 *    prune" contract that stops a narrow update from wiping the gallery.
 *
 * 2. `Content-Type` is NEVER set. The browser must set it itself so it can append the multipart
 *    boundary; setting it by hand produces a body the server cannot parse. Only `Authorization`
 *    goes on these requests, which is why the existing headers object was safe to keep.
 */
async function toRiskFormData(payload: any): Promise<FormData> {
  const media: FileObject[] = Array.isArray(payload?.media) ? payload.media : [];
  const newFiles = media.filter(file => file && !file.id);

  const { media: _dropped, ...rest } = payload || {};
  const body = {
    ...rest,
    // The survivors. The server soft-deletes every active row whose id is not in here.
    existingMedia: media.filter(file => file && file.id).map(file => ({ id: file.id })),
  };

  const formData = new FormData();
  formData.append('data', JSON.stringify(body));

  // Sequential rather than `Promise.all`: the parts must be appended in INDEX ORDER, because the
  // server numbers `displayIndex` from the order it walks `req.files` and that order is the
  // gallery's order and the export's order.
  for (let index = 0; index < newFiles.length; index++) {
    const file = newFiles[index];
    const blob = await fetch(file.url).then(response => response.blob());
    formData.append(`newMedia${index}`, blob, attachmentFilename(file, blob, index));
  }

  return formData;
}

/**
 * The filename for one multipart part.
 *
 * It has to END IN A REAL EXTENSION: the server's `generateBlobName` takes the blob's extension
 * from this string, so a part named `photo` produces an extensionless blob that browsers and the
 * PDF embedder both have to guess at. The extension is taken from the MIME type rather than from
 * whatever the phone called the file, because that is the type the server records as `mediaType`.
 *
 * The doctor's own filename is preserved as the stem where there is one — it is what the gallery
 * shows under the thumbnail, and "IMG_4471" is more use to them than "risk-photo-3".
 */
function attachmentFilename(file: FileObject, blob: Blob, index: number): string {
  const mime = file.type || blob.type || 'image/jpeg';
  const extension = (mime.split('/')[1] || 'jpg').split('+')[0];
  const stem = (file.name || '').replace(/\.[^.]+$/, '').trim() || `risk-photo-${Date.now()}-${index}`;
  return `${stem}.${extension}`;
}
